import { Handset, IosArrowRight } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import dayjs from 'dayjs';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { TextLink } from '@/components/text-link';
import { joinSgcmSite } from '@/utils/url/join/public';
import { getPhoneUrl } from '@/utils/url/phone';

import { ShareUrlButton } from '../../../share-url-button';
import {
  PROMOTION_DEALER_INFO_ICON_SIZE,
  PROMOTION_MODEL_COVER_IMAGE_HEIGHT,
  PROMOTION_MODEL_COVER_IMAGE_LOGO_HEIGHT,
  PROMOTION_MODEL_COVER_IMAGE_WIDTH,
  PROMOTION_MORE_PROMOS_ICON_SIZE,
  PROMOTION_POSTED_ON_DATE_FORMAT,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const Content: Component = ({
  dealer: {
    id: dealerId,
    name: dealerName,
    url: dealerUrl,
    contactNumbers: dealerContactNumberList,
  },
  description,
  image,
  models,
  endsOn,
  postedOn,
  link,
  isAllModels,
  make,
  onErrorReport,
  setOpen,
}) => {
  const pathname = usePathname();

  const handleMorePromosClick = (e: React.MouseEvent) => {
    if (pathname === '/promotions') {
      e.preventDefault();
      setOpen?.(false);
    } else {
      window.open(`${joinSgcmSite('/new-cars/promotions')}`, '_self');
    }
  };

  return (
    <div
      className={styles.containerRoot}>
      <div
        className={styles.containerPromotionContent}>
        <div className={styles.containerPromotionTitleContent}>
          <a href={dealerUrl}>
            <Image
              alt="Promotion image of vehicle"
              className={styles.imageVehicleCover}
              height={image?.includes('/logos/')
                ? PROMOTION_MODEL_COVER_IMAGE_HEIGHT
                : PROMOTION_MODEL_COVER_IMAGE_LOGO_HEIGHT}
              src={image}
              width={PROMOTION_MODEL_COVER_IMAGE_WIDTH} />
          </a>
          <div className={styles.containerTitleContentDetail}>
            <div className={styles.containerTitleAndShare}>
              <div className={styles.textTitle}>
                Promotion at
                {' '}
                <a
                  className={styles.linkDealerName}
                  href={dealerUrl}>
                  {dealerName}
                </a>
              </div>
            </div>
            <div>
              <ul
                className={styles.listDealerInfo}>
                <li
                  className={cx(
                    styles.containerDealerInfo,
                    styles.isPhoneNumber,
                  )}>
                  <div
                    className={styles.containerDealerInfoIcon}>
                    <Handset
                      size={PROMOTION_DEALER_INFO_ICON_SIZE} />
                  </div>
                  <ul
                    className={styles.listDealerPhoneNumber}>
                    {
                      dealerContactNumberList
                        .slice()
                        .reverse()
                        .map(phoneNumber => (
                          <li
                            key={phoneNumber}
                            className={styles.containerDealerPhoneNumber}>
                            <Link
                              className={styles.textDealerPhoneNumber}
                              href={`${getPhoneUrl(phoneNumber)}`}>
                              {`${phoneNumber.slice(0, 4)} ${phoneNumber.slice(4)}`}
                            </Link>
                          </li>
                        ))
                    }
                  </ul>
                </li>
              </ul>
            </div>
            <div
              className={styles.containerPromotionDate}>
              <span className={styles.textRed}>
                {endsOn}
              </span>
              <span className={styles.textGray}>
                Posted on:
                {' '}
                <time>
                  {dayjs(postedOn).format(PROMOTION_POSTED_ON_DATE_FORMAT)}
                </time>
              </span>
            </div>
            <div className={styles.containerMorePromosAndShare}>
              <button
                className={styles.morePromosLink}
                onClick={handleMorePromosClick}>
                More Promos
                <IosArrowRight
                  size={PROMOTION_MORE_PROMOS_ICON_SIZE} />
              </button>
              <div
                className={styles.containerShareLink}>
                <ShareUrlButton
                  link={link} />
              </div>
            </div>
          </div>
        </div>
        <div
        // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: description,
          }}
          className={styles.containerPromotionDescription} />
        <div className={styles.containerApplicable}>
          <div className={styles.textApplicable}>Applicable Models</div>
          { isAllModels
            ? (
              <ul
                className={styles.listPromotionModelLink}>
                <li
                  key="all models"
                  className={styles.linkPromotionModel}>
                  <TextLink
                    href={`${joinSgcmSite(`/new_cars/newcars_listing.php?DID=${dealerId}`)}`}
                    target="_self"
                    text={`All ${make} Models`} />
                </li>
              </ul>
            )
            : (
              <ul
                className={styles.listPromotionModelLink}>
                {
                  models.map(({ title, link: modelLink }) => (
                    <li
                      key={modelLink}
                      className={styles.linkPromotionModel}>
                      <TextLink
                        href={modelLink}
                        text={title} />
                    </li>
                  ))
                }
              </ul>
            ) }

        </div>
        <span
          className={cx(
            styles.textFooter,
            styles.textDisclaimer,
          )}>
          Disclaimer: Dealer
          {'\''}
          s Terms & Conditions applies on this promotion. Sgcarmart.com doesn
          {'\''}
          t
          {
            '\n'
          }
          assume any responsibility for errors or omissions or warrant the
          accuracy of this information.
        </span>
        <button
          className={styles.textReportError}
          onClick={() => {
            onErrorReport?.();
          }}>
          Report Error
        </button>
      </div>
    </div>
  );
};
